import { Calendar, cn } from '@noorden-insurtech/alba-design-system';
import { X } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

/**
 * Normaliza o root do `Calendar` para alinhar com os demais filtros:
 * o root é um `flex flex-col gap-2`, então o `gap` interno o deixava ~8px mais
 * alto que os campos de 44px (h-11). `!gap-0` remove esse espaço (precisa de
 * `!important` para vencer o `gap-2` do design system) e forçamos o `control`
 * (a caixa visível) a h-11 e largura total.
 */
const FIELD_BASE_CLASS = '!gap-0 [&_[data-part=control]]:h-11';
const FIXED_WIDTH_CLASS = '[&_[data-part=control]]:w-72';
const FULL_WIDTH_CLASS = '[&_[data-part=control]]:w-full';
const CLEARABLE_CLASS =
  '[&_[data-part=trigger]_svg]:hidden [&_[data-part=trigger]]:pr-9 [&_[data-part=trigger]>span]:min-w-0 [&_[data-part=trigger]>span]:truncate';

export interface DateRangeFilterFieldProps {
  /** Início do período, em ISO `yyyy-MM-dd`. */
  from?: string;
  /** Fim do período, em ISO `yyyy-MM-dd`. */
  to?: string;
  /**
   * Emitido quando o intervalo fecha (duas datas) ou quando é limpo. Datas em
   * ISO `yyyy-MM-dd` (string vazia quando limpo).
   */
  onChange: (_range: { from: string; to: string }) => void;
  placeholder?: string;
  className?: string;
  /**
   * Ocupa toda a largura do contêiner (ex.: célula de um grid de filtros) em
   * vez da largura fixa padrão de 18rem.
   */
  fullWidth?: boolean;
}

/**
 * O `Calendar` do design system é UTC nas duas pontas: emite
 * `DateValue.toDate('UTC')` (meia-noite UTC do dia clicado) e lê o `value` por
 * `date.toISOString().split('T')[0]` (parte UTC). Converter no fuso local
 * derrubava um dia em qualquer offset negativo — em UTC−3, clicar 20/07 virava
 * `2026-07-19`. Por isso ancoramos as duas conversões em UTC.
 */
const toJsDate = (iso?: string): Date | undefined => {
  if (!iso) return undefined;
  const parsed = DateTime.fromISO(iso, { zone: 'utc' });
  return parsed.isValid ? parsed.toJSDate() : undefined;
};

const toIsoDate = (date: Date): string | null =>
  DateTime.fromJSDate(date, { zone: 'utc' }).toISODate();

const propsToValue = (from?: string, to?: string): Date[] =>
  [toJsDate(from), toJsDate(to)].filter(
    (date): date is Date => date instanceof Date
  );

/**
 * Filtro de período baseado no `Calendar` do Alba Design System em modo
 * intervalo (`selectionMode='range'`). As datas trafegam em ISO `yyyy-MM-dd`.
 *
 * A seleção em andamento (1ª data escolhida) fica em estado local e NÃO é
 * comitada para o filtro — só emitimos `onChange` quando o intervalo fecha
 * (duas datas) ou é limpo. Isso evita colapsar o range para um único dia, o que
 * impedia a escolha da segunda data.
 */
export function DateRangeFilterField({
  from,
  to,
  onChange,
  placeholder = 'Período',
  className,
  fullWidth = false,
}: DateRangeFilterFieldProps) {
  const [value, setValue] = useState<Date[]>(() => propsToValue(from, to));

  // Re-sincroniza quando o filtro muda por fora (ex.: limpar/reset via URL).
  useEffect(() => {
    setValue(propsToValue(from, to));
  }, [from, to]);

  const handleValueChange = (dates: Date[]) => {
    const next = dates ?? [];
    setValue(next);

    const isoDates = next
      .map(toIsoDate)
      .filter((iso): iso is string => Boolean(iso));

    if (isoDates.length === 0) {
      onChange({ from: '', to: '' });
    } else if (isoDates.length >= 2) {
      onChange({ from: isoDates[0], to: isoDates[1] });
    }
    // isoDates.length === 1 → seleção em andamento; aguardamos a 2ª data.
  };

  const handleClear = () => {
    setValue([]);
    onChange({ from: '', to: '' });
  };

  const hasValue = Boolean(from || to);

  return (
    <div className={cn(fullWidth ? 'w-full' : 'w-fit', className)}>
      {/*
        O `relative` precisa ficar numa caixa com a MESMA largura do campo. O
        wrapper externo herda o `className` do chamador (normalmente `w-full`),
        enquanto o controle é forçado a `w-72` e transborda esse wrapper — então
        ancorar o botão lá fora media o `right` a partir da borda errada e o X
        caía em cima do texto. Este `w-fit` encolhe até a largura real do campo.
      */}
      <div className={cn('relative', fullWidth ? 'w-full' : 'w-fit')}>
        <Calendar
          value={value}
          onValueChange={handleValueChange}
          placeholder={placeholder}
          selectionMode='range'
          className={cn(
            FIELD_BASE_CLASS,
            fullWidth ? FULL_WIDTH_CLASS : FIXED_WIDTH_CLASS,
            hasValue && CLEARABLE_CLASS,
            fullWidth ? 'w-full' : 'w-fit'
          )}
        />
        {hasValue && (
          <button
            type='button'
            aria-label='Limpar período'
            onMouseDown={e => e.preventDefault()}
            onClick={e => {
              e.stopPropagation();
              handleClear();
            }}
            className='absolute right-3 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center rounded p-1 text-alba-gray-600 transition-colors hover:bg-alba-gray-200 hover:text-alba-black-500'
          >
            <X className='h-4 w-4' strokeWidth={1.6} />
          </button>
        )}
      </div>
    </div>
  );
}
